import React, { useState } from "react";
import {
  Box,
  Stack,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Button,
  Alert,
  Collapse,
} from "@mui/material";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import { getDeviceStationId, isRealPhone } from "../utils/global_functions";
import { Chip } from "@mui/material";



import { apiFetch } from "../utils/apiFetch";

// Preset status options shown in the dropdown.
// value = what gets sent to the backend, label = what the user sees.
const STATUS_OPTIONS = [
  { value: "late", label: "Late" },
  { value: "visit_before_company", label: "Visit before company" },
  { value: "accident", label: "Accident" },
  { value: "other", label: "Other" },
];

// Standalone component — not tied to a fingerprint tap.
// The employee picks a status from the dropdown, optionally adds a note,
// and sends it independently. Self-contained: manages its own state and
// its own submit.
export default function EmployeeStatus({online}) {
  const [selected, setSelected] = useState("");
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);
  const [feedback, setFeedback] = useState({ open: false, message: "", severity: "success" });

  const API_BASE_URL =
    localStorage.getItem("saved_sett_api_url") || "http://localhost:5100";

  const handleSend = async () => {
    if (!selected) {
      setFeedback({ open: true, message: "Pick a status first.", severity: "warning" });
      return;
    }

    setSending(true);
    setFeedback({ open: false, message: "", severity: "success" });

    const empId = localStorage.getItem("shared_empid");
    const now = new Date();
    const payload = {
      empId,
      status: selected,
      note: note.trim(),
      date: now.toISOString().split("T")[0],
      time: now.toTimeString().split(" ")[0],
      stationId: getDeviceStationId(),
      isPhone: isRealPhone(),
    };

    try {
      const res = await apiFetch(`${API_BASE_URL}/api/employee-status`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error("Request failed");

      setFeedback({ open: true, message: "Status sent.", severity: "success" });
      setSelected("");
      setNote("");
    } catch (err) {
      console.error("Failed to send employee status:", err);
      setFeedback({
        open: true,
        message: "Failed to send status. Check your connection.",
        severity: "error",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: 560,
        borderRadius: 2.5,
        border: "1px solid #b2dfdb",
        bgcolor: "rgba(255,255,255,0.95)",
        p: 1.25,
        mb: 1.5,
      }}
    >
      {/* <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 1 }}>
        <ReportProblemIcon sx={{ color: "#00796b", fontSize: 16 }} />
        <Typography variant="caption" sx={{ fontWeight: 700, fontSize: 12.5 }}>
          Employee status
        </Typography>
      </Stack> */}

       <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={0.75}>
          <ReportProblemIcon sx={{ color: "#00796b", fontSize: 16 }} />
          <Typography variant="caption" sx={{ fontWeight: 700, fontSize: 12.5 }}>
            Employee status
          </Typography>
        </Stack>
        <Chip
          label={online ? "Online" : "Offline"}
          color={online ? "success" : "warning"}
          size="small"
          sx={{ fontWeight: 700, fontSize: 11, height: 20 }}
        />
      </Stack>

      {/* Dropdown + note always on the same row, phone included */}
      <Stack direction="row" spacing={0.75} sx={{ mb: 1 }}>
        <FormControl
          size="small"
          sx={{
            minWidth: { xs: 92, sm: 150 },
            flex: { xs: "0 0 38%", sm: "0 0 40%" },
          }}
        >
          <InputLabel sx={{ fontSize: { xs: 11, sm: 12.5 } }}>Status</InputLabel>
          <Select
            label="Status"
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
            sx={{
              fontSize: { xs: 11, sm: 12.5 },
              "& .MuiSelect-select": { py: 0.85, pr: "24px !important" },
            }}
          >
            {STATUS_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: { xs: 11, sm: 12.5 } }}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          label="Note (optional)"
          fullWidth
          size="small"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Describe what happened..."
          sx={{
            flex: { xs: "1 1 62%", sm: "1 1 60%" },
            "& .MuiInputBase-input": { fontSize: { xs: 11, sm: 12.5 }, py: 0.85 },
            "& .MuiInputLabel-root": { fontSize: { xs: 11, sm: 12.5 } },
          }}
        />
      </Stack>

      <Button
        variant="contained"
        fullWidth
        size="small"
        disabled={sending}
        onClick={handleSend}
        sx={{
          bgcolor: "#00796b",
          fontWeight: 700,
          fontSize: 12,
          py: 0.5,
          "&:hover": { bgcolor: "#004d40" },
        }}
      >
        {sending ? "Sending..." : "Send status"}
      </Button>

      <Collapse in={feedback.open} timeout={200} unmountOnExit>
        <Alert
          severity={feedback.severity}
          sx={{ mt: 1, py: 0, fontSize: 12 }}
          onClose={() => setFeedback((f) => ({ ...f, open: false }))}
        >
          {feedback.message}
        </Alert>
      </Collapse>
    </Box>
  );
}